import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Card, CardContent, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Collapse,
  Divider, Checkbox, LinearProgress, Alert, Grid,
} from '@mui/material';
import {
  Add as AddIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Check as DoneIcon, CalendarToday as DateIcon, Assignment as TaskIcon,
  Delete as DeleteIcon, EventNote as EventIcon, Article as ContentIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { authFetch } from '../../utils/authHelpers';
import { BACKEND_URL } from '../../utils/apiConfig';
import { formatDateISO } from '../../utils/weekUtils';

const AGENDA_ICONS = { review: '📋', brainstorm: '💡', update: '📊', decisions: '⚖️', actions: '✅' };
const STATUS_COLORS = {
  DRAFT: { label: 'Concept', color: '#6b7280', bg: '#f3f4f6' },
  FINALIZED: { label: 'Afgerond', color: '#059669', bg: '#d1fae5' },
};

export default function MarketingMeetings({ onRefresh }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDate, setCreateDate] = useState(formatDateISO(new Date()));
  const [saving, setSaving] = useState(false);

  // Context data for meetings: upcoming content + events
  const [upcomingContent, setUpcomingContent] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const fetchMeetings = useCallback(async () => {
    try {
      const [mtgRes, contentRes, eventsRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/marketing/meetings`),
        authFetch(`${BACKEND_URL}/Api/marketing/content-items?status=IDEA&status=CONCEPT&status=IN_PRODUCTION&status=REVIEW&limit=20`),
        authFetch(`${BACKEND_URL}/Api/marketing/events?from=${formatDateISO(new Date())}&limit=10`),
      ]);
      if (mtgRes.ok) setMeetings(await mtgRes.json());
      if (contentRes.ok) setUpcomingContent(await contentRes.json());
      if (eventsRes.ok) setUpcomingEvents(await eventsRes.json());
    } catch (err) { console.error('[Meetings] Fetch error:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const fetchDetail = async (id) => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/meetings/${id}`);
      if (res.ok) setDetail(await res.json());
    } catch (err) { console.error('[Meetings] Detail error:', err); }
  };

  const handleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); setDetail(null); }
    else { setExpandedId(id); fetchDetail(id); }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/meetings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: createTitle || undefined, meetingDate: createDate }),
      });
      if (res.ok) {
        const created = await res.json();
        toast.success('Meeting aangemaakt');
        setCreateOpen(false); setCreateTitle('');
        fetchMeetings();
        // Auto-expand the new meeting
        setExpandedId(created.id);
        fetchDetail(created.id);
        if (onRefresh) onRefresh();
      }
    } catch (err) { toast.error('Aanmaken mislukt'); }
    finally { setSaving(false); }
  };

  const handleSaveAgenda = async (meetingId, agendaItems) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/marketing/meetings/${meetingId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agendaItems }),
      });
      toast.success('Opgeslagen');
    } catch (err) { toast.error('Opslaan mislukt'); }
  };

  const handleSaveNotes = async (meetingId, notes) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/marketing/meetings/${meetingId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
    } catch (err) { console.error('Save notes failed:', err); }
  };

  const handleFinalize = async (meetingId) => {
    try {
      await authFetch(`${BACKEND_URL}/Api/marketing/meetings/${meetingId}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'FINALIZED' }),
      });
      toast.success('Meeting afgerond');
      fetchMeetings(); fetchDetail(meetingId);
    } catch (err) { toast.error('Afronden mislukt'); }
  };

  const handleCreateTasks = async (meetingId, actionItems) => {
    try {
      const res = await authFetch(`${BACKEND_URL}/Api/marketing/meetings/${meetingId}/create-tasks`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actionItems }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.created} taken aangemaakt`);
        fetchDetail(meetingId);
        if (onRefresh) onRefresh();
      }
    } catch (err) { toast.error('Taken aanmaken mislukt'); }
  };

  if (loading) return <Box sx={{ py: 4 }}><LinearProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Marketing Meetings</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
          Nieuwe meeting
        </Button>
      </Box>

      {meetings.length === 0 && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Nog geen meetings. Start je eerste marketing meeting!</Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {meetings.map((mtg) => {
          const isExpanded = expandedId === mtg.id;
          const statusCfg = STATUS_COLORS[mtg.status] || STATUS_COLORS.DRAFT;

          return (
            <Card key={mtg.id} variant="outlined" sx={{ borderLeft: `4px solid ${statusCfg.color}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{mtg.title}</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip icon={<DateIcon sx={{ fontSize: 14 }} />}
                        label={new Date(mtg.meetingDate).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
                        size="small" variant="outlined" />
                      <Chip label={statusCfg.label} size="small" sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 600 }} />
                      {mtg.taskCount > 0 && <Chip icon={<TaskIcon sx={{ fontSize: 14 }} />} label={`${mtg.doneTaskCount}/${mtg.taskCount} taken`} size="small" variant="outlined" />}
                    </Box>
                  </Box>
                  <IconButton onClick={() => handleExpand(mtg.id)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  {detail?.id === mtg.id ? (
                    <MeetingCockpit
                      meeting={detail}
                      upcomingContent={upcomingContent}
                      upcomingEvents={upcomingEvents}
                      onSaveAgenda={(items) => handleSaveAgenda(mtg.id, items)}
                      onSaveNotes={(notes) => handleSaveNotes(mtg.id, notes)}
                      onFinalize={() => handleFinalize(mtg.id)}
                      onCreateTasks={(items) => handleCreateTasks(mtg.id, items)}
                    />
                  ) : (
                    <Box sx={{ py: 2, textAlign: 'center' }}><LinearProgress /></Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nieuwe Marketing Meeting</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Titel (optioneel)" value={createTitle} onChange={(e) => setCreateTitle(e.target.value)}
            placeholder="Marketing Meeting" fullWidth size="small" />
          <TextField label="Datum" type="date" value={createDate} onChange={(e) => setCreateDate(e.target.value)}
            fullWidth size="small" InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Annuleren</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}
            sx={{ bgcolor: '#6366f1' }}>{saving ? 'Bezig...' : 'Aanmaken'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Meeting Cockpit (full-width, with braindump + context) ─────────────────

function MeetingCockpit({ meeting, upcomingContent, upcomingEvents, onSaveAgenda, onSaveNotes, onFinalize, onCreateTasks }) {
  const [agenda, setAgenda] = useState(meeting.agendaItems || []);
  const [braindump, setBraindump] = useState(meeting.notes || '');
  const [newActionTitle, setNewActionTitle] = useState('');
  const [newActionAssignee, setNewActionAssignee] = useState('');
  const [pendingActions, setPendingActions] = useState([]);
  const isDraft = meeting.status === 'DRAFT';

  const updateAgendaItem = (index, field, value) => {
    const updated = [...agenda];
    updated[index] = { ...updated[index], [field]: value };
    setAgenda(updated);
  };

  const addAction = () => {
    if (!newActionTitle.trim()) return;
    setPendingActions([...pendingActions, { title: newActionTitle, assigneeName: newActionAssignee }]);
    setNewActionTitle(''); setNewActionAssignee('');
  };

  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e2e8f0' }}>
      <Grid container spacing={3}>
        {/* Left: Braindump + Agenda */}
        <Grid item xs={12} md={8}>
          {/* Braindump */}
          {isDraft && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fffbeb', borderColor: '#fde68a' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#92400e' }}>
                💭 Braindump — noteer alles wat je wilt bespreken
              </Typography>
              <TextField fullWidth multiline minRows={3} maxRows={8} size="small"
                placeholder="Typ hier je gedachten, onderwerpen, vragen, ideeën..."
                value={braindump} onChange={(e) => setBraindump(e.target.value)}
                onBlur={() => onSaveNotes(braindump)}
                sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} />
            </Paper>
          )}
          {!isDraft && braindump && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>💭 Notities</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{braindump}</Typography>
            </Paper>
          )}

          {/* Agenda items */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#6366f1' }}>📋 Agenda</Typography>
          {agenda.map((item, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 1.5, borderLeft: `3px solid ${idx < 3 ? '#6366f1' : idx === 3 ? '#f59e0b' : '#10b981'}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {AGENDA_ICONS[item.type] || '📌'} {item.title}
              </Typography>
              <TextField fullWidth multiline minRows={2} size="small" placeholder="Notities..."
                value={item.notes || ''} onChange={(e) => updateAgendaItem(idx, 'notes', e.target.value)}
                disabled={!isDraft} sx={{ mb: 1 }} />
              {(item.type === 'decisions' || item.type === 'actions') && (
                <TextField fullWidth size="small" placeholder="Besluit / conclusie..."
                  value={item.decision || ''} onChange={(e) => updateAgendaItem(idx, 'decision', e.target.value)}
                  disabled={!isDraft} />
              )}
            </Paper>
          ))}

          {isDraft && (
            <Button size="small" onClick={() => onSaveAgenda(agenda)}
              sx={{ textTransform: 'none', color: '#6366f1', mb: 2 }}>💾 Agenda opslaan</Button>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Action items */}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#10b981' }}>✅ Actiepunten → Taken</Typography>

          {meeting.tasks?.map((task) => (
            <Box key={task.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
              <Checkbox size="small" checked={task.status === 'DONE'} disabled sx={{ p: 0.5 }} />
              <Typography variant="body2" sx={{ flex: 1, textDecoration: task.status === 'DONE' ? 'line-through' : 'none' }}>{task.title}</Typography>
              {task.assigneeName && <Chip label={task.assigneeName} size="small" variant="outlined" />}
            </Box>
          ))}

          {isDraft && (
            <>
              {pendingActions.map((action, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>• {action.title}</Typography>
                  {action.assigneeName && <Chip label={action.assigneeName} size="small" />}
                  <IconButton size="small" onClick={() => setPendingActions(pendingActions.filter((_, i) => i !== idx))}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              ))}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField size="small" placeholder="Nieuw actiepunt..." value={newActionTitle}
                  onChange={(e) => setNewActionTitle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addAction(); }}
                  sx={{ flex: 1 }} />
                <TextField size="small" placeholder="Wie?" value={newActionAssignee}
                  onChange={(e) => setNewActionAssignee(e.target.value)} sx={{ width: 140 }} />
                <Button size="small" variant="outlined" onClick={addAction} sx={{ textTransform: 'none' }}>+</Button>
              </Box>
              {pendingActions.length > 0 && (
                <Button variant="contained" startIcon={<TaskIcon />}
                  onClick={() => { onCreateTasks(pendingActions); setPendingActions([]); }}
                  sx={{ textTransform: 'none', bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, mb: 2 }}>
                  {pendingActions.length} actiepunt{pendingActions.length > 1 ? 'en' : ''} omzetten naar taken
                </Button>
              )}
              <Divider sx={{ my: 2 }} />
              <Button variant="outlined" startIcon={<DoneIcon />} onClick={onFinalize}
                sx={{ textTransform: 'none', color: '#059669', borderColor: '#059669' }}>Meeting afronden</Button>
            </>
          )}
          {meeting.status === 'FINALIZED' && (
            <Alert severity="success" sx={{ mt: 1 }}>Meeting afgerond</Alert>
          )}
        </Grid>

        {/* Right sidebar: Context — upcoming content + events */}
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ContentIcon sx={{ fontSize: 18, color: '#6366f1' }} /> Actieve content ({upcomingContent?.length || 0})
            </Typography>
            {upcomingContent?.length === 0 && <Typography variant="caption" color="text.secondary">Geen actieve content items</Typography>}
            {upcomingContent?.slice(0, 8).map((item) => (
              <Box key={item.id} sx={{ py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{item.title}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                  <Chip label={item.status} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                  {item.channel?.length > 0 && <Chip label={item.channel[0]} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                  {item.ownerName && <Typography variant="caption" color="text.secondary">{item.ownerName}</Typography>}
                </Box>
              </Box>
            ))}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EventIcon sx={{ fontSize: 18, color: '#f59e0b' }} /> Aankomende events ({upcomingEvents?.length || 0})
            </Typography>
            {upcomingEvents?.length === 0 && <Typography variant="caption" color="text.secondary">Geen aankomende evenementen</Typography>}
            {upcomingEvents?.slice(0, 6).map((evt) => (
              <Box key={evt.id} sx={{ py: 0.5, borderBottom: '1px solid #f1f5f9' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{evt.name}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3 }}>
                  {evt.startDate && <Chip label={new Date(evt.startDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />}
                  {evt.eventType && <Chip label={evt.eventType} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />}
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
